import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import noop from 'lodash/noop';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  useGetReportMetadata,
} from 'pages/Dashboard/pages/Reports/hooks';
import {
  DateRange,
} from 'types/dateRange';
import {
  normalizeFilterableSettings,
} from 'core/Filters/util';
import Button from 'pages/Dashboard/components/Button';
import Skeleton from 'core/components/Skeleton';
import {
  ReportSettingsDTO,
} from 'dtos/reportSettingsDTO';
import dayjs from 'utils/dayjs';
import Table from 'core/components/VirtualTable';
import {
  columns,
} from 'pages/Dashboard/pages/Reports/ReportDialog/columns';
import Input from 'pages/Dashboard/components/Input';
import uniq from 'lodash/uniq';
import SelectableProvider from 'core/context/selectable';
import compact from 'lodash/compact';
import TabFilter from 'views/EMR/components/TabFilter';

function RowSkeleton() {
  return (
    <div className="flex justify-between items-center gap-6 py-1 border-b border-gray-200">
      <Skeleton className="!w-[35rem] !h-5 !rounded-full" variant="rounded" />
      <div className="flex gap-8">
        <Skeleton className="!w-[6rem] !h-5 !rounded-full" variant="rounded" />
        <Skeleton className="!w-[4rem] !h-5 !rounded-full mr-5" variant="rounded" />
      </div>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="flex flex-col gap-5 w-full">
      <div className="flex flex-row items-center gap-4">
        <Skeleton className="!w-[16rem] !min-w-[10rem] !h-8" variant="rounded" />
        <div className="flex gap-3">
          <Skeleton className="!h-7 !w-20 !rounded-full" variant="rounded" />
          <Skeleton className="!h-7 !w-20 !rounded-full" variant="rounded" />
          <Skeleton className="!h-7 !w-20 !rounded-full" variant="rounded" />
        </div>
      </div>
      <div className="w-full h-[22rem] rounded-lg overflow-hidden">
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
      </div>
    </div>
  );
}

type ReportFilterValue =
  | Date
  | DateRange
  | string
  | number
  | boolean
  | null
  | unknown[]
  | Record<string, unknown>;

export type ReportForm = {
  filter: Record<string, ReportFilterValue>;
};

type Props = {
  onClose: () => void;
};

export default function ReportDialog({ onClose }: Props) {
  const {
    data,
    isFetching,
  } = useGetReportMetadata();
  const reportSettings = data?.reportSettings ?? [];
  const [selectedCategory, selectCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filterComponents = useMemo(() => (
    reportSettings.flatMap((report) => normalizeFilterableSettings(report.parameters ?? []))
  ), [reportSettings]);
  const defaultValues = useMemo<ReportForm>(() => {
    const now = dayjs().endOf('day');
    const defaultFilter = Object.fromEntries(filterComponents.map(({ name, filterType }) => {
      switch (filterType) {
        case 'daterange':
          return [name, {
            min: now.subtract(1, 'months').toDate(),
            max: now.toDate(),
          }];
        case 'date':
          return [name, now.toDate()];
        case 'month':
          return [name, now.startOf('month').toDate()];
        case 'year':
          return [name, now.startOf('year').toDate()];
        default:
          return [name, undefined];
      }
    }));

    return { filter: defaultFilter };
  }, [filterComponents]);
  const form = useForm<ReportForm>({
    mode: 'onChange',
    defaultValues,
  });
  const { reset } = form;

  useEffect(() => {
    if (!isFetching) {
      reset(defaultValues, { keepDirtyValues: true });
    }
  }, [defaultValues, isFetching, reset]);

  const categories = useMemo(() => (
    compact([...uniq(reportSettings?.map((report) => report?.category) ?? [])])
  ), [reportSettings]);

  const filteredReports = useMemo(() => {
    const hasAllSelected = selectedCategory === 'All';
    return reportSettings?.filter((report) => (
      (hasAllSelected || selectedCategory === report?.category)
      && ((report?.reportDisplayName ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
    ));
  }, [reportSettings, searchQuery, selectedCategory]);

  const tabFilterOptions = useMemo(() => ['All', ...categories], [categories]);

  return (
    <Dialog
      open
      title="Run Report"
      onClose={onClose}
      onConfirm={noop}
      maxWidth="xl"
      className="overflow-y-hidden overflow-x-hidden !space-y-0 gap-2 !w-[56rem]"
      disableEnforceFocus
    >
      <FormProvider {...form}>
        <div className={clsx('flex flex-1 flex-col populate-form min-h-[10rem] gap-4 w-full', {
          'flex items-center justify-center': isFetching,
        })}
        >
          {isFetching
            ? <FormSkeleton />
            : (
              <div className="flex flex-col gap-5 !w-full">
                <div className="flex flex-row items-center gap-4">
                  <Input
                    type="text"
                    placeholder="Search reports..."
                    className="w-[16rem] min-w-[10rem]"
                    value={searchQuery}
                    onChange={(value) => {
                      setSearchQuery(value);
                    }}
                  />
                  <TabFilter
                    options={tabFilterOptions}
                    onUpdate={(option) => {
                      selectCategory(option);
                    }}
                  />
                </div>
                <div className="w-full h-[22rem] rounded-lg overflow-hidden">
                  <SelectableProvider>
                    <Table<ReportSettingsDTO>
                      data={filteredReports}
                      columns={columns}
                      className="!h-full"
                      classes={{
                        outerHeader: 'hidden',
                        header: 'hidden',
                        row: 'hover:!border-none',
                        outerRow: 'hover:!border-populate-border hover:bg-gray-50 hover:!border-r-transparent hover:!border-l-transparent hover:!rounded-none hover:!shadow-none',
                      }}
                    />
                  </SelectableProvider>
                </div>
              </div>
            )}
        </div>
        <div className="flex justify-end items-center gap-2">
          <Button
            variant="outlined"
            type="button"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </FormProvider>
    </Dialog>
  );
}
